import React, { useState, useEffect } from 'react';
import { API_URL } from '../../config';
import CreaturesTable from './CreaturesTable';
import CreaturesFilters from './CreaturesFilters';
import CreatureCompare from './CreatureCompare';
import './CreaturesBrowser.css';

function CreaturesBrowser() {
  const [creatures, setCreatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: '',
    dungeon: '',
    slots: '',
    class: '',
    combat: '',
    minTaming: '',
    maxTaming: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0
  });
  const [selectedForComparison, setSelectedForComparison] = useState([]);
  const [showCompareModal, setShowCompareModal] = useState(false);
  const [filterOptions, setFilterOptions] = useState({
    dungeons: [],
    classes: [],
    combatTypes: [],
    slots: [1, 2, 3, 4]
  });

  useEffect(() => {
    fetchFilterOptions();
  }, []);

  useEffect(() => {
    fetchCreatures();
  }, [filters, pagination.page]);

  const fetchFilterOptions = async () => {
    try {
      const response = await fetch(`${API_URL}/api/creatures/filters/options`);
      if (response.ok) {
        const data = await response.json();
        setFilterOptions(data);
      }
    } catch (error) {
      console.error('Failed to fetch filter options:', error);
    }
  };

  const fetchCreatures = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v !== ''))
      });

      const response = await fetch(`${API_URL}/api/creatures?${params}`);
      if (response.ok) {
        const data = await response.json();
        setCreatures(data.creatures);
        setPagination(prev => ({ ...prev, ...data.pagination }));
      }
    } catch (error) {
      console.error('Failed to fetch creatures:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to page 1
  };

  const handleCompareToggle = (creatureId) => {
    setSelectedForComparison(prev => {
      if (prev.includes(creatureId)) {
        return prev.filter(id => id !== creatureId);
      } else if (prev.length < 5) {
        return [...prev, creatureId];
      }
      return prev;
    });
  };

  const handleCompare = () => {
    if (selectedForComparison.length >= 2) {
      setShowCompareModal(true);
    }
  };

  return (
    <div className="creatures-browser">
      <div className="browser-header">
        <h1>UO Outlands Tameable Creatures</h1>
        <p className="subtitle">Browse and compare {pagination.total} tameable creatures</p>
      </div>

      <div className="browser-layout">
        <CreaturesFilters
          filters={filters}
          filterOptions={filterOptions}
          onFilterChange={handleFilterChange}
        />

        <div className="browser-main">
          {selectedForComparison.length > 0 && (
            <div className="comparison-toolbar">
              <span>{selectedForComparison.length} creatures selected</span>
              <button
                className="btn-compare"
                onClick={handleCompare}
                disabled={selectedForComparison.length < 2}
              >
                Compare Selected ({selectedForComparison.length})
              </button>
              <button
                className="btn-clear"
                onClick={() => setSelectedForComparison([])}
              >
                Clear Selection
              </button>
            </div>
          )}

          <CreaturesTable
            creatures={creatures}
            loading={loading}
            selectedForComparison={selectedForComparison}
            onCompareToggle={handleCompareToggle}
          />

          {/* Pagination */}
          {!loading && pagination.totalPages > 1 && (
            <div className="pagination">
              <button
                onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
                disabled={pagination.page === 1}
              >
                Previous
              </button>
              <span>Page {pagination.page} of {pagination.totalPages}</span>
              <button
                onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
                disabled={pagination.page === pagination.totalPages}
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>

      {showCompareModal && (
        <CreatureCompare
          creatureIds={selectedForComparison}
          onClose={() => setShowCompareModal(false)}
          apiUrl={API_URL}
        />
      )}
    </div>
  );
}

export default CreaturesBrowser;
